import React, { useEffect, useRef, useState, useCallback } from 'react';
import VoiceTyping from '../../components/VoiceTyping';
import FileMenu from './FileMenu';

const ToolBtn = ({ label, children, onClick, active = false }) => (
  <button
    type="button"
    aria-label={label}
    aria-pressed={active}
    title={label}
    onClick={onClick}
    className={[
      'w-7 h-7 rounded flex items-center justify-center',
      active
        ? 'bg-blue-100 dark:bg-blue-500/15 text-blue-700 dark:text-blue-300'
        : 'hover:bg-slate-100 dark:hover:bg-slate-800/40 text-slate-600 dark:text-slate-300',
    ].join(' ')}
  >
    {children}
  </button>
);

// Build a helper that proxies to the Quill editor exposed by DocumentBody via getEditor(0).
function makeEditorOps(editorRef) {
  const getEditor = () => editorRef?.current?.getEditor?.(0) || null;

  const getCurrentFormat = () => {
    const editor = getEditor();
    if (!editor) return {};
    const range = editor.getSelection();
    if (!range) return {};
    return editor.getFormat(range.index, Math.max(range.length, 0)) || {};
  };

  // Toggle inline mark (bold/italic/underline). Mirrors EditableDocumentPage:
  // reads current state and applies the negation so a second click removes it.
  // The 'user' source is critical: DocumentBody's text-change listener filters
  // out 'api' events so programmatic setContents during patient switches
  // doesn't trigger autosave. Toolbar actions ARE user-initiated, so they
  // need 'user' to keep autosave firing on formatting changes.
  const toggleMark = (name) => () => {
    const editor = getEditor();
    if (!editor) return;
    const range = editor.getSelection();
    if (!range) return;
    const currentFormat = editor.getFormat(range.index, Math.max(range.length, 0)) || {};
    editor.format(name, !currentFormat[name], 'user');
  };

  // Apply a block-level / value format (header, list, align, font, size).
  // Same 'user' source requirement as toggleMark — without it, the autosave
  // wouldn't fire when the user changes paragraph style, font, list type, etc.
  const applyValue = (name, value) => () => {
    const editor = getEditor();
    if (!editor) return;
    if (editor.getSelection() == null) editor.focus();
    editor.format(name, value, 'user');
  };

  return { getCurrentFormat, toggleMark, applyValue };
}

const PARAGRAPH_OPTIONS = [
  { label: 'Normal',    value: false },
  { label: 'Heading 1', value: 1 },
  { label: 'Heading 2', value: 2 },
  { label: 'Heading 3', value: 3 },
];

const FONT_OPTIONS = [
  { label: 'Inter',     value: false },        // Default = Quill's base font (Inter via clinic.css)
  { label: 'Serif',     value: 'serif' },
  { label: 'Monospace', value: 'monospace' },
];

const SIZE_OPTIONS = [
  { label: '10px', value: '10px' },
  { label: '12px', value: '12px' },
  { label: '14px', value: false },           // Default
  { label: '16px', value: '16px' },
  { label: '18px', value: '18px' },
  { label: '24px', value: '24px' },
  { label: '32px', value: '32px' },
];

export default function DocumentToolbar({
  editorRef,
  autosaveStatus,
  templateName = 'NP',
  readOnly = false,
  // File menu handlers — sits to the left of the heading-size dropdown so
  // it reads as the classic top-left "File" entry in a word-processor
  // toolbar. Parent decides what's real and what's stubbed.
  onNewDocument,
  onOpenDocument,
  onSaveDocument,
  onShareDocument,
  onVersionHistory,
  onSaveVersion,
  onDownloadDocx,
  onReformat,
  onPrint,
  onCommentClick,
  // Optional: omitted (button hidden) for read-only roles.
  onAssignTaskClick,
  safeShareMode = false,
  safeShareMethod = 'mask',
  isDeidentifying = false,
  onToggleSafeShare,
  onSafeShareMethodChange,
}) {
  const { toggleMark, applyValue, getCurrentFormat } = makeEditorOps(editorRef);
  const savedAt = autosaveStatus?.savedAt;

  // Re-render the toolbar whenever the cursor moves or formats change so the
  // Paragraph/Font/Size dropdowns + B/I/U highlights reflect what's at the
  // caret. Quill exposes 'selection-change' (caret moves) and 'text-change'
  // (formatting / content edits) — both should trigger a refresh.
  // `tick` is in the effect deps so the polling fallback (when Quill hasn't
  // mounted yet) keeps trying until the editor is reachable.
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const editor = editorRef?.current?.getEditor?.(0);
    if (!editor) {
      const t = setTimeout(() => setTick(n => n + 1), 250);
      return () => clearTimeout(t);
    }
    const bump = () => setTick(n => (n + 1) % 1e9);
    editor.on('selection-change', bump);
    editor.on('text-change', bump);
    return () => {
      editor.off('selection-change', bump);
      editor.off('text-change', bump);
    };
  }, [editorRef, tick]);

  // Read the format under the current selection. Recomputed each render — the
  // useEffect above re-renders whenever selection or content changes.
  const fmt = (typeof window !== 'undefined') ? getCurrentFormat() : {};

  // Voice-typing → editor bridge. Mirrors EditableDocumentPage.handleVoiceTypingResult:
  // interim results are inserted at the cursor and tracked so subsequent interim
  // events replace the preview; final segments commit and clear the tracking.
  const voiceInterimRangeRef = useRef(null);
  const handleVoiceTypingResult = useCallback((transcriptText, opts = {}) => {
    const editor = editorRef?.current?.getEditor?.(0);
    if (!editor) return;
    if (opts.keepInterim) {
      voiceInterimRangeRef.current = null;
      return;
    }
    const isFinal = opts.isFinal !== false;
    if (voiceInterimRangeRef.current) {
      const { index, length } = voiceInterimRangeRef.current;
      if (length > 0) {
        try { editor.deleteText(index, length, 'user'); } catch (_) {}
      }
      voiceInterimRangeRef.current = null;
    }
    if (!transcriptText) return;
    const selection = editor.getSelection();
    const cursorPosition = selection ? selection.index : editor.getLength();
    if (isFinal) {
      const text = transcriptText + ' ';
      editor.insertText(cursorPosition, text, 'user');
      editor.setSelection(cursorPosition + text.length);
    } else {
      editor.insertText(cursorPosition, transcriptText, 'user');
      voiceInterimRangeRef.current = { index: cursorPosition, length: transcriptText.length };
      editor.setSelection(cursorPosition + transcriptText.length);
    }
  }, [editorRef]);

  // In read mode the toolbar still renders so the user can see formatting
  // state under the caret, but interactive controls (dropdowns, B/I/U,
  // alignment, lists) are disabled to make it unmistakable that formatting
  // can't change. Quill itself ignores `.format()` calls while disabled,
  // so this is a UX nicety rather than a hard gate.
  return (
    <div
      data-clinic-toolbar
      data-read-only={readOnly ? 'true' : 'false'}
      className={[
        // flex-wrap so the formatting controls flow onto a second row when the
        // panel is too narrow to fit them in one line, instead of overflowing.
        'border-b border-slate-200 dark:border-slate-800/60 px-6 py-2 flex flex-wrap items-center gap-x-1 gap-y-1.5 text-slate-600 dark:text-slate-300',
        // Soft horizontal gradient + slight inner shadow so the toolbar
        // reads as a distinct surface from the header bar above and the
        // document body below.
        'bg-gradient-to-r from-indigo-50 via-sky-50 to-emerald-50 dark:from-indigo-500/[0.08] dark:via-sky-500/[0.06] dark:to-emerald-500/[0.08]',
        'shadow-[inset_0_-1px_0_rgba(15,23,42,0.04)] dark:shadow-[inset_0_-1px_0_rgba(255,255,255,0.04)]',
        // Non-File-menu controls are inert in read mode, but do NOT dim the
        // whole bar with `opacity-50` — CSS opacity applies to the entire
        // subtree and would grey out the File menu too (which must stay usable
        // for Version History / Print / Download while the doc is locked).
        readOnly ? 'pointer-events-none select-none' : '',
      ].join(' ')}
      aria-disabled={readOnly}
    >
      {/* File menu — must sit OUTSIDE the readOnly pointer-events lock so
          the user can still Save / Print / Download / browse Version
          History when the document is in read mode. The lock applies to
          the rest of the toolbar (formatting toggles, dropdowns) because
          those would no-op anyway. */}
      <div className="pointer-events-auto opacity-100">
        <FileMenu
          onNewDocument={onNewDocument}
          onOpenDocument={onOpenDocument}
          onSaveDocument={onSaveDocument}
          onShare={onShareDocument}
          onVersionHistory={onVersionHistory}
          onSaveVersion={onSaveVersion}
          onDownloadDocx={onDownloadDocx}
          onReformat={onReformat}
          onPrint={onPrint}
          readOnly={readOnly}
        />
      </div>
      <div className="w-px h-5 bg-slate-200 dark:bg-slate-800/60 mx-1" />

      {/* Paragraph style */}
      <select
        aria-label="Paragraph style"
        value={fmt.header === undefined ? '' : String(fmt.header)}
        onChange={(e) => {
          const v = e.target.value === 'false' || e.target.value === '' ? false : Number(e.target.value);
          applyValue('header', v)();
        }}
        className="text-xs bg-transparent border border-slate-200 dark:border-slate-800/60 rounded px-2 py-1 text-slate-600 dark:text-slate-300"
      >
        {PARAGRAPH_OPTIONS.map(o => (
          <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
        ))}
      </select>

      {/* Font family */}
      <select
        aria-label="Font family"
        value={fmt.font === undefined ? '' : String(fmt.font)}
        onChange={(e) => {
          const v = e.target.value === 'false' || e.target.value === '' ? false : e.target.value;
          applyValue('font', v)();
        }}
        className="text-xs bg-transparent border border-slate-200 dark:border-slate-800/60 rounded px-2 py-1 text-slate-600 dark:text-slate-300"
      >
        {FONT_OPTIONS.map(o => (
          <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
        ))}
      </select>

      {/* Font size */}
      <select
        aria-label="Font size"
        value={fmt.size === undefined ? '' : String(fmt.size)}
        onChange={(e) => {
          const v = e.target.value === 'false' || e.target.value === '' ? false : e.target.value;
          applyValue('size', v)();
        }}
        className="text-xs bg-transparent border border-slate-200 dark:border-slate-800/60 rounded px-2 py-1 text-slate-600 dark:text-slate-300"
      >
        {SIZE_OPTIONS.map(o => (
          <option key={String(o.value)} value={String(o.value)}>{o.label}</option>
        ))}
      </select>

      <div className="w-px h-5 bg-slate-200 dark:bg-slate-800/60 mx-1" />

      <ToolBtn label="Bold" onClick={toggleMark('bold')} active={!!fmt.bold}>
        <span className="font-bold">B</span>
      </ToolBtn>
      <ToolBtn label="Italic" onClick={toggleMark('italic')} active={!!fmt.italic}>
        <span className="italic">I</span>
      </ToolBtn>
      <ToolBtn label="Underline" onClick={toggleMark('underline')} active={!!fmt.underline}>
        <span className="underline">U</span>
      </ToolBtn>
      <ToolBtn label="Strikethrough" onClick={toggleMark('strike')} active={!!fmt.strike}>
        <span className="line-through">S</span>
      </ToolBtn>

      <div className="w-px h-5 bg-slate-200 dark:bg-slate-800/60 mx-1" />

      <ToolBtn label="Bulleted list" onClick={applyValue('list', fmt.list === 'bullet' ? false : 'bullet')} active={fmt.list === 'bullet'}>
        {/* Mirrors Quill's `list-bullet.svg` so the icon matches the toolbar in EditableDocumentPage. */}
        <svg className="w-4 h-4" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <line x1="6" x2="15" y1="4" y2="4" />
          <line x1="6" x2="15" y1="9" y2="9" />
          <line x1="6" x2="15" y1="14" y2="14" />
          <line x1="3" x2="3" y1="4" y2="4" />
          <line x1="3" x2="3" y1="9" y2="9" />
          <line x1="3" x2="3" y1="14" y2="14" />
        </svg>
      </ToolBtn>
      <ToolBtn label="Ordered list" onClick={applyValue('list', fmt.list === 'ordered' ? false : 'ordered')} active={fmt.list === 'ordered'}>
        {/* Mirrors Quill's `list-ordered.svg` — three lines + a tiny "1" + "2" + "3". */}
        <svg className="w-4 h-4" viewBox="0 0 18 18" fill="none" stroke="currentColor" strokeLinecap="round">
          <line strokeWidth="2" x1="7" x2="15" y1="4" y2="4" />
          <line strokeWidth="2" x1="7" x2="15" y1="9" y2="9" />
          <line strokeWidth="2" x1="7" x2="15" y1="14" y2="14" />
          <line strokeWidth="1" x1="2.5" x2="4.5" y1="5.5" y2="5.5" />
          <path strokeWidth="0" fill="currentColor" d="M3.5,6A0.5,0.5,0,0,1,3,5.5V3.085l-0.276.138A0.5,0.5,0,0,1,2.053,3c-0.124-.247-0.023-0.324.224-0.447l1-.5A0.5,0.5,0,0,1,4,2.5v3A0.5,0.5,0,0,1,3.5,6Z" />
          <path strokeWidth="1" d="M4.5,10.5h-2c0-.234,1.85-1.076,1.85-2.234A0.959,0.959,0,0,0,2.5,8.156" />
          <path strokeWidth="1" d="M2.5,14.846a0.959,0.959,0,0,0,1.85-.109A0.7,0.7,0,0,0,3.75,14a0.688,0.688,0,0,0,.6-0.736,0.959,0.959,0,0,0-1.85-.109" />
        </svg>
      </ToolBtn>

      <div className="w-px h-5 bg-slate-200 dark:bg-slate-800/60 mx-1" />

      <ToolBtn label="Align left" onClick={applyValue('align', false)} active={!fmt.align}>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h10M4 18h16" /></svg>
      </ToolBtn>
      <ToolBtn label="Align center" onClick={applyValue('align', 'center')} active={fmt.align === 'center'}>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M7 12h10M4 18h16" /></svg>
      </ToolBtn>
      <ToolBtn label="Align right" onClick={applyValue('align', 'right')} active={fmt.align === 'right'}>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M10 12h10M4 18h16" /></svg>
      </ToolBtn>
      <ToolBtn label="Add Comment" onClick={onCommentClick}>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" /></svg>
      </ToolBtn>
      {onAssignTaskClick && (
        <ToolBtn label="Assign task" onClick={onAssignTaskClick}>
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
        </ToolBtn>
      )}
      {/* Task #213 Phase 1.5 — Add Problem button MOVED to header row
          (PatientHeaderBar action cluster, next to Restore / Reset to Seed).
          Kept the noop here as a comment so a search for the old location
          finds the new one. Implementation in PatientHeaderBar.js. */}

      <div className="w-px h-5 bg-slate-200 dark:bg-slate-800/60 mx-1" />

      {/* Undo / Redo — Leejoe 2026-06-29. Originally landed in
          PatientHeaderBar next to Restore but the right home is the
          editor toolbar with the rest of the formatting controls, right
          beside the voice-typing mic. Google Docs convention. The
          underlying Quill `history` module is enabled in DocumentBody
          (userOnly:true so remote collab syncs don't pollute the local
          undo stack). */}
      <ToolBtn
        label="Undo (Cmd/Ctrl+Z)"
        onClick={() => { try { editorRef?.current?.undo?.(); } catch (_) {} }}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h11a5 5 0 010 10h-3M3 10l5-5M3 10l5 5" />
        </svg>
      </ToolBtn>
      <ToolBtn
        label="Redo (Cmd/Ctrl+Shift+Z)"
        onClick={() => { try { editorRef?.current?.redo?.(); } catch (_) {} }}
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 10H10a5 5 0 000 10h3M21 10l-5-5M21 10l-5 5" />
        </svg>
      </ToolBtn>

      <div className="w-px h-5 bg-slate-200 dark:bg-slate-800/60 mx-1" />

      {/* Voice typing MOVED — Leejoe 2026-06-29. Lives in the top white
          bar (DoctorPortalBar's VoiceDictateChip) so it's never blocked
          by the note and is always discoverable. The component there
          dispatches `vmai:voice-dictate-result` window events;
          MedStudentClinicPage listens and writes into the editor. The
          handleVoiceTypingResult helper above is now a dead branch and
          can be cleaned up after a validation cycle. */}

      <div className="w-px h-5 bg-slate-200 dark:bg-slate-800/60 mx-1 pointer-events-auto opacity-100" />

      {/* Safe-Share Mode Controls */}
      <div className="flex items-center gap-1 pointer-events-auto opacity-100">
        <button
          type="button"
          onClick={onToggleSafeShare}
          disabled={isDeidentifying}
          className={[
            'text-xs px-2.5 py-1 rounded font-semibold flex items-center gap-1 transition-colors border',
            safeShareMode
              ? 'bg-teal-500 border-teal-500 text-white hover:bg-teal-600'
              : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50 dark:bg-slate-800 dark:border-slate-700 dark:text-slate-300'
          ].join(' ')}
          style={{ cursor: isDeidentifying ? 'not-allowed' : 'pointer' }}
        >
          {isDeidentifying ? (
            <span>Processing...</span>
          ) : safeShareMode ? (
            <><span>🔒</span> <span>Safe-Share Active</span></>
          ) : (
            <><span>🔓</span> <span>Safe-Share</span></>
          )}
        </button>
        {!safeShareMode && (
          <select
            value={safeShareMethod}
            onChange={(e) => onSafeShareMethodChange?.(e.target.value)}
            disabled={isDeidentifying}
            className="text-xs bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded px-1.5 py-1 text-slate-600 dark:text-slate-300 outline-none"
          >
            <option value="mask">Mask</option>
            <option value="redact">Redact</option>
            <option value="hash">Hash</option>
          </select>
        )}
      </div>

      <div className="flex-1" />
      <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 live-dot" />
        Auto-saved{savedAt ? ` · ${savedAt}` : ''} · Template: {templateName}
      </span>
    </div>
  );
}
